/**
 * Collection Grid JavaScript
 * Handles filter interactions, sort dropdown, and mobile drawers
 */

(function() {
  'use strict';

  // DOM Elements
  const filterToggles = document.querySelectorAll('[data-filter-toggle]');
  const filterCheckboxes = document.querySelectorAll('[data-filter-checkbox]');
  const dateSliders = document.querySelectorAll('[data-date-slider]');
  const clearAllBtn = document.querySelector('[data-clear-all]');
  const activeFiltersContainer = document.querySelector('[data-active-filters]');
  const sortDropdown = document.querySelector('[data-sort-dropdown]');
  const sortTrigger = document.querySelector('[data-sort-trigger]');
  const sortOptions = document.querySelectorAll('[data-sort-options] a');
  const mobileFilterBtn = document.querySelector('[data-open-filters]');
  const mobileSortBtn = document.querySelector('[data-open-sort]');
  const mobileDrawers = document.querySelectorAll('[data-mobile-drawer]');
  const closeDrawerBtns = document.querySelectorAll('[data-close-drawer]');
  const applyFiltersBtn = document.querySelector('[data-apply-filters]');
  const mobileFiltersContent = document.querySelector('[data-mobile-filters-content]');

  /**
   * Initialize filter group toggles
   */
  function initFilterToggles() {
    filterToggles.forEach(toggle => {
      toggle.addEventListener('click', function() {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !isExpanded);
        
        const content = this.nextElementSibling;
        if (content) {
          content.classList.toggle('collapsed', isExpanded);
        }
        scheduleStickinessUpdate();
      });
    });

    // Initialize show more/less buttons
    const showMoreBtns = document.querySelectorAll('[data-show-more]');
    showMoreBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const filterGroup = this.closest('.filters__options');
        const hiddenOptions = filterGroup.querySelectorAll('.filters__option--hidden');

        if (hiddenOptions.length > 0) {
          hiddenOptions.forEach(option => {
            option.classList.remove('filters__option--hidden');
          });
          this.textContent = 'Show less';
        } else {
          const allOptions = filterGroup.querySelectorAll('.filters__option');
          allOptions.forEach((option, index) => {
            if (index >= 12) {
              option.classList.add('filters__option--hidden');
            }
          });
          this.textContent = 'Show more';
        }
        scheduleStickinessUpdate();
      });
    });
  }

  /**
   * Handle filter checkbox changes
   */
  function initFilterCheckboxes() {
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', function() {
        updateActiveFilters();
        applyFilters();
      });
    });

    // Date range sliders (release date)
    dateSliders.forEach(initDateSlider);
  }

  /**
   * Format YYYY-MM-DD into e.g. "7 July 2026"
   */
  function formatDateLong(iso) {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const d = new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)));
    if (isNaN(d.getTime())) return iso;
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return d.getUTCDate() + ' ' + months[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
  }

  /**
   * Wire up a single dual-handle date slider.
   */
  function initDateSlider(slider) {
    const minInput = slider.querySelector('[data-date-slider-min]');
    const maxInput = slider.querySelector('[data-date-slider-max]');
    const fill = slider.querySelector('[data-date-slider-fill]');
    const display = slider.querySelector('[data-date-slider-display]');
    const applyBtn = slider.querySelector('[data-date-slider-apply]');
    const allDates = (slider.dataset.dateRangeAll || '').split(',').map(d => d.trim()).filter(Boolean);
    if (!minInput || !maxInput || allDates.length === 0) return;

    // Set dateActive based on whether date params are already in the URL
    const paramName = slider.dataset.dateRangeParam;
    const urlParams = new URLSearchParams(window.location.search);
    const alreadyActive = paramName && urlParams.getAll(paramName).length > 0;
    slider.dataset.dateActive = alreadyActive ? 'true' : 'false';

    const maxIndex = allDates.length - 1;
    const initialMin = parseInt(minInput.value, 10) || 0;
    const initialMax = parseInt(maxInput.value, 10) || maxIndex;
    slider.dataset.initialMin = String(initialMin);
    slider.dataset.initialMax = String(initialMax);

    function clamp(v) {
      if (v < 0) return 0;
      if (v > maxIndex) return maxIndex;
      return v;
    }

    function refresh() {
      let minVal = clamp(parseInt(minInput.value, 10));
      let maxVal = clamp(parseInt(maxInput.value, 10));
      // Prevent crossover
      if (minVal > maxVal) {
        if (document.activeElement === minInput) {
          maxVal = minVal;
          maxInput.value = String(maxVal);
        } else {
          minVal = maxVal;
          minInput.value = String(minVal);
        }
      }
      // Track fill
      const minPct = maxIndex === 0 ? 0 : (minVal / maxIndex) * 100;
      const maxPct = maxIndex === 0 ? 100 : (maxVal / maxIndex) * 100;
      if (fill) {
        fill.style.left = minPct + '%';
        fill.style.right = (100 - maxPct) + '%';
      }
      // Display text
      if (display) {
        display.textContent = formatDateLong(allDates[minVal]) + ' – ' + formatDateLong(allDates[maxVal]);
      }
      // Store chosen dates on the wrapper for URL expansion
      slider.dataset.selectedMin = allDates[minVal];
      slider.dataset.selectedMax = allDates[maxVal];
      // Apply button is always enabled
      if (applyBtn) {
        applyBtn.disabled = false;
      }
    }

    minInput.addEventListener('input', refresh);
    maxInput.addEventListener('input', refresh);

    if (applyBtn) {
      applyBtn.addEventListener('click', function() {
        slider.dataset.dateActive = 'true';
        applyFilters();
      });
    }

    refresh();
  }

  /**
   * Update active filters display
   */
  function updateActiveFilters() {
    if (!activeFiltersContainer) return;

    activeFiltersContainer.innerHTML = '';
    
    const checkedFilters = document.querySelectorAll('[data-filter-checkbox]:checked');
    
    checkedFilters.forEach(checkbox => {
      const label = checkbox.closest('.filters__option').querySelector('.filters__option-label');
      if (label) {
        const tag = createFilterTag(label.textContent, checkbox);
        activeFiltersContainer.appendChild(tag);
      }
    });

    // Date range slider active tag (only when range differs from full span AND is currently applied in URL)
    const urlParams = new URLSearchParams(window.location.search);
    dateSliders.forEach(slider => {
      const paramName = slider.dataset.dateRangeParam;
      if (!paramName) return;
      const hasActive = urlParams.getAll(paramName).length > 0;
      if (!hasActive) return;

      const minDate = slider.dataset.selectedMin;
      const maxDate = slider.dataset.selectedMax;
      if (!minDate && !maxDate) return;

      const text = 'Release: ' + formatDateLong(minDate) + ' – ' + formatDateLong(maxDate);
      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = 'filters__selected-tag';
      tag.innerHTML = text +
        ' <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      tag.addEventListener('click', function() {
        resetDateSlider(slider);
        applyFilters();
      });
      activeFiltersContainer.appendChild(tag);
    });
  }

  /**
   * Reset a date slider to its full span.
   */
  function resetDateSlider(slider) {
    const minInput = slider.querySelector('[data-date-slider-min]');
    const maxInput = slider.querySelector('[data-date-slider-max]');
    if (!minInput || !maxInput) return;
    minInput.value = minInput.min;
    maxInput.value = maxInput.max;
    slider.dataset.initialMin = minInput.min;
    slider.dataset.initialMax = maxInput.max;
    slider.dataset.dateActive = 'false';
    minInput.dispatchEvent(new Event('input'));
  }

  /**
   * Create a filter tag element
   */
  function createFilterTag(text, checkbox) {
    const tag = document.createElement('button');
    tag.type = 'button';
    tag.className = 'filters__selected-tag';
    tag.innerHTML = `
      ${text}
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    tag.addEventListener('click', function() {
      checkbox.checked = false;
      updateActiveFilters();
      applyFilters();
    });
    
    return tag;
  }

  /**
   * Clear all filters
   */
  function initClearAll() {
    if (!clearAllBtn) return;

    clearAllBtn.addEventListener('click', function() {
      filterCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      dateSliders.forEach(resetDateSlider);
      updateActiveFilters();
      applyFilters();
    });
  }

  /**
   * Expand the active date slider range into individual filter params.
   * Skipped only when the slider was explicitly reset/cleared.
   */
  function appendDateRangeParams(params, scope) {
    const root = scope || document;
    const sliders = root.querySelectorAll('[data-date-slider]');
    sliders.forEach(slider => {
      // Only include date params when the slider is marked as active
      if (slider.dataset.dateActive !== 'true') return;

      const minInput = slider.querySelector('[data-date-slider-min]');
      const maxInput = slider.querySelector('[data-date-slider-max]');
      if (!minInput || !maxInput) return;

      const minIdx = parseInt(minInput.value, 10);
      const maxIdx = parseInt(maxInput.value, 10);

      const paramName = slider.dataset.dateRangeParam;
      const allDates = (slider.dataset.dateRangeAll || '').split(',').map(d => d.trim()).filter(Boolean);

      for (let i = minIdx; i <= maxIdx; i++) {
        if (allDates[i]) params.append(paramName, allDates[i]);
      }
    });
  }

  /**
   * Apply filters by updating URL and reloading
   */
  function applyFilters() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    
    // Keep sort parameter if exists
    const sortBy = url.searchParams.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    // Add checked filter values
    const checkedFilters = document.querySelectorAll('[data-filter-checkbox]:checked');
    checkedFilters.forEach(checkbox => {
      const name = checkbox.name;
      const value = checkbox.value;
      params.append(name, value);
    });

    // Add date-group filter values
    appendDateRangeParams(params);

    // Handle price range
    const priceMin = document.querySelector('[data-price-min]');
    const priceMax = document.querySelector('[data-price-max]');
    
    if (priceMin && priceMin.value) {
      params.set(priceMin.name, priceMin.value * 100);
    }
    if (priceMax && priceMax.value) {
      params.set(priceMax.name, priceMax.value * 100);
    }

    // Navigate to filtered URL
    window.location.href = `${url.pathname}?${params.toString()}`;
  }

  /**
   * Initialize sort dropdown
   */
  function initSortDropdown() {
    if (!sortDropdown || !sortTrigger) return;

    sortTrigger.addEventListener('click', function(e) {
      e.stopPropagation();
      sortDropdown.classList.toggle('open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
      if (!sortDropdown.contains(e.target)) {
        sortDropdown.classList.remove('open');
      }
    });

    // Handle sort option selection
    sortOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        e.preventDefault();
        const sortValue = this.dataset.sortValue;
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortValue);
        window.location.href = url.toString();
      });
    });
  }

  /**
   * Initialize mobile drawers
   */
  function initMobileDrawers() {
    // Open filters drawer
    if (mobileFilterBtn) {
      mobileFilterBtn.addEventListener('click', function() {
        openDrawer('filters');
        cloneFiltersToMobile();
      });
    }

    // Open sort drawer
    if (mobileSortBtn) {
      mobileSortBtn.addEventListener('click', function() {
        openDrawer('sort');
      });
    }

    // Close drawer buttons
    closeDrawerBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        closeAllDrawers();
      });
    });

    // Apply filters button
    if (applyFiltersBtn) {
      applyFiltersBtn.addEventListener('click', function() {
        applyMobileFilters();
      });
    }
  }

  /**
   * Open a specific drawer
   */
  function openDrawer(type) {
    const drawer = document.querySelector(`[data-mobile-drawer="${type}"]`);
    if (drawer) {
      drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Close all drawers
   */
  function closeAllDrawers() {
    mobileDrawers.forEach(drawer => {
      drawer.classList.remove('open');
    });
    document.body.style.overflow = '';
  }

  /**
   * Clone desktop filters to mobile drawer
   */
  function cloneFiltersToMobile() {
    if (!mobileFiltersContent) return;

    const desktopFilters = document.querySelector('.collection-grid__filters');
    if (desktopFilters) {
      mobileFiltersContent.innerHTML = desktopFilters.innerHTML;
      
      // Re-initialize toggles in mobile drawer
      const mobileToggles = mobileFiltersContent.querySelectorAll('[data-filter-toggle]');
      mobileToggles.forEach(toggle => {
        toggle.addEventListener('click', function() {
          const isExpanded = this.getAttribute('aria-expanded') === 'true';
          this.setAttribute('aria-expanded', !isExpanded);
          
          const content = this.nextElementSibling;
          if (content) {
            content.classList.toggle('collapsed', isExpanded);
          }
        });
      });

      // Re-initialize show more/less buttons in mobile drawer
      const mobileShowMoreBtns = mobileFiltersContent.querySelectorAll('[data-show-more]');
      mobileShowMoreBtns.forEach(btn => {
        btn.addEventListener('click', function() {
          const filterGroup = this.closest('.filters__options');
          const hiddenOptions = filterGroup.querySelectorAll('.filters__option--hidden');

          if (hiddenOptions.length > 0) {
            hiddenOptions.forEach(option => {
              option.classList.remove('filters__option--hidden');
            });
            this.textContent = 'Show less';
          } else {
            const allOptions = filterGroup.querySelectorAll('.filters__option');
            allOptions.forEach((option, index) => {
              if (index >= 12) {
                option.classList.add('filters__option--hidden');
              }
            });
            this.textContent = 'Show more';
          }
        });
      });

      // Re-initialize date sliders in mobile drawer
      const mobileSliders = mobileFiltersContent.querySelectorAll('[data-date-slider]');
      mobileSliders.forEach(slider => {
        if (slider instanceof HTMLElement) {
          // Inherit dateActive state from original desktop slider
          const originalSlider = desktopFilters.querySelector('[data-date-slider]');
          if (originalSlider instanceof HTMLElement) {
            slider.dataset.dateActive = originalSlider.dataset.dateActive || 'false';
          }
          initDateSlider(slider);
        }
      });

      // Re-initialize clear all button in mobile drawer
      const mobileClearAllBtn = mobileFiltersContent.querySelector('[data-clear-all]');
      if (mobileClearAllBtn) {
        mobileClearAllBtn.addEventListener('click', function() {
          const mobileCheckboxes = mobileFiltersContent.querySelectorAll('[data-filter-checkbox]');
          mobileCheckboxes.forEach(checkbox => {
            if (checkbox instanceof HTMLInputElement) {
              checkbox.checked = false;
            }
          });
          const mobileDateSliders = mobileFiltersContent.querySelectorAll('[data-date-slider]');
          mobileDateSliders.forEach(slider => {
            if (slider instanceof HTMLElement) {
              resetDateSlider(slider);
            }
          });
          applyMobileFilters();
        });
      }

      // Re-initialize filter tag removal buttons in mobile drawer
      const mobileFilterTags = mobileFiltersContent.querySelectorAll('.filters__selected-tag');
      mobileFilterTags.forEach(tag => {
        tag.addEventListener('click', function() {
          // For checkbox filters, uncheck the corresponding checkbox
          const labelText = tag.textContent.replace(/\s*×.*$/, '').trim();
          const mobileOptions = mobileFiltersContent.querySelectorAll('.filters__option');
          mobileOptions.forEach(option => {
            const label = option.querySelector('.filters__option-label');
            if (label && label.textContent === labelText) {
              const checkbox = option.querySelector('[data-filter-checkbox]');
              if (checkbox instanceof HTMLInputElement) {
                checkbox.checked = false;
              }
            }
          });
          // For date tags, reset the date slider
          if (labelText.startsWith('Release:')) {
            const mobileDateSlider = mobileFiltersContent.querySelector('[data-date-slider]');
            if (mobileDateSlider instanceof HTMLElement) {
              resetDateSlider(mobileDateSlider);
            }
          }
          applyMobileFilters();
        });
      });
    }
  }

  /**
   * Apply filters from mobile drawer
   */
  function applyMobileFilters() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    
    // Keep sort parameter if exists
    const sortBy = url.searchParams.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    // Add checked filter values from mobile drawer
    const mobileCheckboxes = mobileFiltersContent.querySelectorAll('[data-filter-checkbox]:checked');
    mobileCheckboxes.forEach(checkbox => {
      const name = checkbox.name;
      const value = checkbox.value;
      params.append(name, value);
    });

    // Add date-group filter values from mobile drawer
    appendDateRangeParams(params, mobileFiltersContent);

    // Handle price range in mobile
    const priceMin = mobileFiltersContent.querySelector('[data-price-min]');
    const priceMax = mobileFiltersContent.querySelector('[data-price-max]');
    
    if (priceMin && priceMin.value) {
      params.set(priceMin.name, priceMin.value * 100);
    }
    if (priceMax && priceMax.value) {
      params.set(priceMax.name, priceMax.value * 100);
    }

    // Navigate to filtered URL
    window.location.href = `${url.pathname}?${params.toString()}`;
  }

  /**
   * Initialize pagination expand (ellipsis button reveals all pages)
   */
  function initPaginationExpand() {
    const expandBtn = document.querySelector('[data-pagination-expand]');
    if (!expandBtn) return;

    expandBtn.addEventListener('click', function() {
      const paginationList = this.closest('[data-pagination-list]');
      if (paginationList) {
        paginationList.classList.toggle('pagination-list--expanded');
      }
    });
  }

  // Debounce timers
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let _resizeTimer;
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let _stickyTimer;

  /**
   * Use native CSS sticky positioning. For a tall sidebar the negative top
   * offset lets it scroll normally until its bottom reaches the viewport;
   * short sidebars stick below the header.
   */
  function updateSidebarStickiness() {
    const filters = /** @type {HTMLElement|null} */ (document.querySelector('.collection-grid__filters'));
    if (!filters) return;

    if (window.innerWidth <= 1024) {
      filters.style.removeProperty('--filters-sticky-top');
      return;
    }

    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 80;
    const gap = 20;
    const topOffset = Math.min(headerHeight + gap, window.innerHeight - filters.offsetHeight - gap);
    filters.style.setProperty('--filters-sticky-top', `${Math.round(topOffset)}px`);
  }

  /**
   * Re-evaluate stickiness after the group collapse/expand transition (0.3s) settles.
   */
  function scheduleStickinessUpdate() {
    clearTimeout(_stickyTimer);
    _stickyTimer = setTimeout(updateSidebarStickiness, 320);
  }

  /**
   * Preserve filter (and other) query params across pagination links.
   * Shopify's paginate URLs and manually-built page links only include ?page=N,
   * dropping active filter params. This rebuilds each pagination link to include
   * the current URL's query params (minus page) alongside the link's own page param.
   */
  function preserveFiltersInPagination() {
    var currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('page');

    if (currentParams.toString() === '') return;

    var paginationLinks = document.querySelectorAll('.collection-grid__pagination a[href]');
    paginationLinks.forEach(function(link) {
      var href = link.getAttribute('href');
      if (!href) return;

      var linkUrl = new URL(href, window.location.origin);
      var pageParam = linkUrl.searchParams.get('page');

      var newParams = new URLSearchParams(currentParams);
      if (pageParam) newParams.set('page', pageParam);

      link.setAttribute('href', linkUrl.pathname + '?' + newParams.toString());
    });
  }

  /**
   * Initialize on page load
   */
  function init() {
    initFilterToggles();
    initFilterCheckboxes();
    initClearAll();
    initSortDropdown();
    initMobileDrawers();
    updateActiveFilters();
    initPaginationExpand();
    preserveFiltersInPagination();
    updateSidebarStickiness();
    // Header height is set via JS; re-check once everything (fonts, header) has settled
    window.addEventListener('load', updateSidebarStickiness);

    // Clear all filters button in empty state
    const emptyClearAllBtn = document.querySelector('[data-clear-all-filters]');
    if (emptyClearAllBtn) {
      emptyClearAllBtn.addEventListener('click', function() {
        filterCheckboxes.forEach(checkbox => {
          if (checkbox instanceof HTMLInputElement) {
            checkbox.checked = false;
          }
        });
        dateSliders.forEach(resetDateSlider);
        updateActiveFilters();
        applyFilters();
      });
    }

    // Re-check on resize (desktop <-> mobile transitions)
    window.addEventListener('resize', function() {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(updateSidebarStickiness, 300);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
